/*
 * The tray's state machine: what is staged for one composer, in which order, and how far each file
 * has got (spec 07 §4 "States"). One instance per draft target, kept alive across a remount, so
 * walking away from a conversation and coming back does not abort a hundred-megabyte upload.
 *
 * Three rules shape it. Only three uploads are on the wire at once, because the server refuses the
 * fourth of a session outright rather than queueing it. A dropped connection is retried exactly
 * once, and the retry asks `HEAD` first, so the file the server already holds is never sent twice.
 * And a refusal that names a rule — too large, wrong scope, quota — is never retried automatically:
 * the same bytes would break the same rule.
 *
 * The store is deliberately a plain observable and not a React state: the tray, the send button and
 * the drop overlay all read it, and an upload that continues while nothing is mounted must have
 * somewhere to continue in.
 */

import type { AttachmentRef, TargetKey } from "../../drafts/types.ts"
import { admit, type AttachmentLimits, type Refusal } from "./limits.ts"
import { readyRefs, stagedFromRefs, type StagedError, type StagedFile } from "./types.ts"
import { cancelUpload, headUpload, putUpload, type UploadRecord } from "./uploadClient.ts"

/** Uploads on the wire at once. The server refuses the fourth of a session (spec 07 §4). */
export const MAX_IN_FLIGHT = 3

/** How often a transient failure is answered by the machine rather than by the person. */
export const AUTO_RETRIES = 1

type Listener = () => void

/** What the tray does to the world outside itself; a test hands its own in. */
export interface TrayDeps {
	put: typeof putUpload
	head: typeof headUpload
	cancel: typeof cancelUpload
	uuid: () => string
	limits?: AttachmentLimits
}

const browserDeps: TrayDeps = {
	put: putUpload,
	head: headUpload,
	cancel: cancelUpload,
	uuid: () => crypto.randomUUID(),
}

/** One staged file plus the parts of it nothing outside this file may see. */
interface Entry {
	file: StagedFile
	/** The bytes, kept until the upload succeeded so a retry needs no second drop. */
	body?: File
	abort?: AbortController
	attempts: number
}

export class TrayStore {
	private readonly scope: TargetKey
	private readonly deps: TrayDeps
	private entries: Entry[] = []
	private snapshot: StagedFile[] = []
	private listeners = new Set<Listener>()
	private refusals: Refusal[] = []
	/** Set once anything has been staged or seeded, so a late draft never overwrites the tray. */
	private touched = false
	private sync: ((refs: AttachmentRef[]) => void) | undefined

	constructor(scope: TargetKey, deps: Partial<TrayDeps> = {}) {
		this.scope = scope
		this.deps = { ...browserDeps, ...deps }
	}

	// ---- reading ----

	/** The tray, in send order. The array identity changes exactly when something changed. */
	files(): readonly StagedFile[] {
		return this.snapshot
	}

	/** The files a drop was refused for, newest batch first; cleared by the next successful drop. */
	rejected(): readonly Refusal[] {
		return this.refusals
	}

	/** True while a file is still on its way up: the send waits for this to go false. */
	busy(): boolean {
		return this.snapshot.some((f) => f.state === "queued" || f.state === "uploading")
	}

	/** True while a file needs an answer from the person; the send is blocked until it has one. */
	blocked(): boolean {
		return this.snapshot.some((f) => f.state === "failed")
	}

	/**
	 * Resolves once nothing is on its way up any more — whether every file arrived, one failed or
	 * the last one was removed. It is what "send when the uploads finish" waits on, so the promise
	 * lives in the tray rather than in an effect that watches it from the outside.
	 */
	settled(): Promise<void> {
		if (!this.busy()) return Promise.resolve()
		return new Promise((done) => {
			const stop = this.subscribe(() => {
				if (this.busy()) return
				stop()
				done()
			})
		})
	}

	subscribe(listener: Listener): () => void {
		this.listeners.add(listener)
		return () => this.listeners.delete(listener)
	}

	/** Registers where the ready files are mirrored — the draft, so a reload finds them again. */
	mirrorTo(sync: (refs: AttachmentRef[]) => void): void {
		this.sync = sync
	}

	// ---- staging ----

	/**
	 * Puts the draft's own attachments into an empty tray. A tray that has already been touched
	 * keeps what it has: the disk answers milliseconds after the first drop, and the draft that was
	 * written before the drop must not undo it.
	 */
	seed(refs: readonly AttachmentRef[]): void {
		if (this.touched || refs.length === 0) return
		this.touched = true
		this.entries = stagedFromRefs(refs).map((file) => ({ file, attempts: 0 }))
		this.publish(false)
	}

	/**
	 * Stages every file the caps admit and starts uploading. A file already in the tray under the
	 * same name and size is skipped: dropping the same selection twice is a slip, not an intent to
	 * send it twice. `note` is the chip's sentence about where the files came from (a long paste).
	 */
	attach(files: readonly File[], note?: string): void {
		this.touched = true
		const fresh = files.filter(
			(file) => !this.entries.some((e) => e.file.name === file.name && e.file.bytes === file.size),
		)
		const { accepted, refused } = admit(
			fresh,
			{ count: this.entries.length, bytes: this.stagedBytes() },
			this.deps.limits,
		)
		this.refusals = refused
		for (const body of accepted) {
			this.entries.push({
				file: {
					uuid: this.deps.uuid(),
					name: body.name,
					bytes: body.size,
					state: "queued",
					progress: 0,
					caption: "",
					...(note === undefined ? {} : { note }),
				},
				body,
				attempts: 0,
			})
		}
		this.publish()
		this.pump()
	}

	/** Takes one file out: the request is aborted and the server forgets the pending row. */
	remove(uuid: string): void {
		const entry = this.entryOf(uuid)
		if (entry === undefined) return
		entry.abort?.abort()
		// A file that was never on the wire has nothing on the server to cancel.
		if (entry.file.state !== "queued") void this.deps.cancel(uuid)
		this.entries = this.entries.filter((e) => e.file.uuid !== uuid)
		this.publish()
		this.pump()
	}

	/** Empties the tray after the message it belonged to has left the composer. */
	clear(): void {
		for (const entry of this.entries) entry.abort?.abort()
		this.entries = []
		this.refusals = []
		this.touched = false
		this.publish()
	}

	/**
	 * Moves one file by `delta` positions and answers where it ended up, or `undefined` when it
	 * could not move. The answer is what the announcement is built from ("logo.png, position 2 of 4").
	 */
	move(uuid: string, delta: number): number | undefined {
		const from = this.entries.findIndex((e) => e.file.uuid === uuid)
		if (from < 0) return undefined
		const to = from + delta
		if (to < 0 || to >= this.entries.length) return undefined
		return this.moveTo(from, to)
	}

	/** Moves the file at `from` to index `to` — the drop half of the same reorder. */
	moveTo(from: number, to: number): number | undefined {
		if (from === to || from < 0 || to < 0 || from >= this.entries.length) return undefined
		const target = Math.min(to, this.entries.length - 1)
		const [entry] = this.entries.splice(from, 1)
		if (entry === undefined) return undefined
		this.entries.splice(target, 0, entry)
		this.publish()
		return target
	}

	/** The caption, which the timeline uses as the image's alt text. */
	setCaption(uuid: string, caption: string): void {
		const entry = this.entryOf(uuid)
		if (entry === undefined) return
		entry.file = { ...entry.file, caption }
		this.publish()
	}

	/** Renames a file — the way a pasted `pasted-1.png` gets a name that says what it is. */
	rename(uuid: string, name: string): void {
		const entry = this.entryOf(uuid)
		if (entry === undefined || name.trim() === "") return
		entry.file = { ...entry.file, name: name.trim() }
		this.publish()
	}

	/** The person's own retry of a failed upload; it starts from the same bytes. */
	retry(uuid: string): void {
		const entry = this.entryOf(uuid)
		if (entry === undefined || entry.file.state !== "failed" || entry.body === undefined) return
		entry.attempts = 0
		entry.file = { ...entry.file, state: "queued", progress: 0 }
		delete entry.file.error
		this.publish()
		this.pump()
	}

	// ---- the upload loop ----

	private pump(): void {
		let inFlight = this.entries.filter((e) => e.file.state === "uploading").length
		for (const entry of this.entries) {
			if (inFlight >= MAX_IN_FLIGHT) return
			if (entry.file.state !== "queued" || entry.body === undefined) continue
			inFlight += 1
			void this.send(entry)
		}
	}

	private async send(entry: Entry): Promise<void> {
		const body = entry.body
		if (body === undefined) return
		const abort = new AbortController()
		entry.abort = abort
		entry.attempts += 1
		this.patch(entry, { state: "uploading", progress: 0 })
		try {
			const record = await this.deps.put({
				uuid: entry.file.uuid,
				scope: this.scope,
				file: body,
				signal: abort.signal,
				onProgress: (fraction) => this.patch(entry, { progress: fraction }),
			})
			this.ready(entry, record)
		} catch (err) {
			await this.failed(entry, err as StagedError)
		} finally {
			this.pump()
		}
	}

	/**
	 * A failed attempt. One transient failure is answered by the machine, and it asks the server
	 * what it already has before it sends the body again — a connection that dropped after the last
	 * byte is the common case, and re-sending would be the expensive way to discover it.
	 */
	private async failed(entry: Entry, error: StagedError): Promise<void> {
		if (!this.entries.includes(entry)) return // removed while it was on the wire
		if (error.retryable && entry.attempts <= AUTO_RETRIES) {
			const stored = await this.deps.head(entry.file.uuid)
			if (stored !== undefined) {
				this.ready(entry, stored)
				return
			}
			this.patch(entry, { state: "queued", progress: 0 })
			return
		}
		this.patch(entry, { state: "failed", error })
	}

	private ready(entry: Entry, record: UploadRecord): void {
		delete entry.body
		delete entry.abort
		const file: StagedFile = {
			...entry.file,
			state: "ready",
			progress: 1,
			id: record.id,
			name: record.filename,
			bytes: record.bytes,
			mime: record.mime,
			kind: record.kind,
			thumbUrl: record.thumbUrl,
		}
		if (record.width !== undefined) file.width = record.width
		if (record.height !== undefined) file.height = record.height
		delete file.error
		entry.file = file
		this.publish()
	}

	// ---- bookkeeping ----

	private entryOf(uuid: string): Entry | undefined {
		return this.entries.find((e) => e.file.uuid === uuid)
	}

	private stagedBytes(): number {
		return this.entries.reduce((sum, e) => sum + e.file.bytes, 0)
	}

	private patch(entry: Entry, over: Partial<StagedFile>): void {
		if (!this.entries.includes(entry)) return
		entry.file = { ...entry.file, ...over }
		this.publish()
	}

	/** One place where the snapshot is rebuilt, the draft is told and the listeners are woken. */
	private publish(mirror = true): void {
		this.snapshot = this.entries.map((e) => e.file)
		if (mirror) this.sync?.(readyRefs(this.snapshot))
		for (const listener of this.listeners) listener()
	}
}

/**
 * One tray per draft target, kept for the life of the tab. The composer is unmounted whenever the
 * conversation changes, and an upload that is still running belongs to the target it was started
 * in, not to the component that happened to be on screen.
 *
 * `trayFor` takes the transport only when it builds the tray, which is what lets a test set one up
 * for a target before the composer that will use it is rendered; the composer itself never passes
 * one and always gets the browser's.
 */
const trays = new Map<string, TrayStore>()

export function trayFor(scope: TargetKey, deps: Partial<TrayDeps> = {}): TrayStore {
	let tray = trays.get(scope)
	if (tray === undefined) {
		tray = new TrayStore(scope, deps)
		trays.set(scope, tray)
	}
	return tray
}

/**
 * Lets go of a tray that holds nothing, when the composer it belonged to is gone. A tray with files
 * in it is kept whatever happens to the component — that is what the registry is for — so this is
 * only the bookkeeping that stops a long session from collecting one empty tray per conversation it
 * ever opened.
 */
export function releaseTray(scope: TargetKey): void {
	if (trays.get(scope)?.files().length === 0) trays.delete(scope)
}
